import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, MailCheck } from 'lucide-react';
import axios from 'axios';

// מצב הקישור עצמו — נבדק מול השרת כבר בטעינת העמוד, כדי שמי שפתח מייל ישן
// יידע מיד שהקישור פג ולא רק אחרי שהקליד סיסמה חדשה פעמיים.
type TokenState = 'checking' | 'valid' | 'expired' | 'invalid';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [tokenState, setTokenState] = useState<TokenState>(token ? 'checking' : 'invalid');
  const [linkError, setLinkError] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/auth/reset-password/validate', { params: { token } });
        if (cancelled) return;
        if (data?.valid) {
          setTokenState('valid');
        } else {
          setTokenState(data?.reason === 'expired' ? 'expired' : 'invalid');
          setLinkError(data?.error || '');
        }
      } catch {
        // אם הבדיקה עצמה נכשלה (רשת/שרת) לא נועלים את המשתמש — נותנים לו לנסות,
        // והשליחה עצמה תחזיר שגיאה מדויקת.
        if (!cancelled) setTokenState('valid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleResend = async () => {
    setResending(true);
    setLinkError('');
    try {
      await axios.post('/api/auth/reset-password/resend', { token });
      setResent(true);
    } catch (err: any) {
      setLinkError(err?.response?.data?.error || 'שליחת הקישור נכשלה, נסו שוב');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('הסיסמאות אינן תואמות');
      return;
    }
    // אותה מדיניות כמו בשרת (utils/passwordPolicy.ts) — כדי לא לשלוח בקשה
    // שנדחית ממילא.
    if (newPassword.length < 8) {
      setError('הסיסמה חייבת להכיל לפחות 8 תווים');
      return;
    }
    if (!/[A-Za-zא-ת]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError('הסיסמה חייבת להכיל לפחות אות אחת וספרה אחת');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { token, newPassword });
      navigate('/login?reset=1');
    } catch (err: any) {
      const reason = err?.response?.data?.reason;
      if (reason === 'expired' || reason === 'invalid') {
        // הקישור מת בין טעינת העמוד לשליחה (למשל נשלח קישור חדש יותר) —
        // מעבירים למסך הקישור השבור עם אפשרות לשלוח חדש.
        setTokenState(reason);
        setLinkError(err?.response?.data?.error || '');
      } else {
        setError(err?.response?.data?.error || 'אירעה שגיאה, נסה שוב');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden bg-gradient-to-br from-[#1565C0] via-[#311B92] to-[#6200EA]"
    >
      <div className="absolute top-[-15%] right-[-10%] w-[40vw] h-[40vw] max-w-[480px] max-h-[480px] rounded-full bg-white/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-md bg-white dark:bg-[#1E1E1E] rounded-3xl shadow-md-4 overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-l from-[#6200EA] to-[#1565C0]" />
        <div className="p-5 md:p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1565C0] to-[#6200EA] shadow-md-3 mb-4">
              <span className="text-2xl font-extrabold text-white">A+</span>
            </div>
            <h1 className="text-3xl font-extrabold bg-gradient-to-l from-[#6200EA] to-[#1565C0] bg-clip-text text-transparent">
              Amutech
            </h1>
            <p className="text-sm text-[#49454F] dark:text-[#CAC4D0] mt-1">
              מערכת ניהול עמותות
            </p>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-[#1C1B1F] dark:text-[#E6E1E5]">איפוס סיסמא</h2>
            <p className="text-sm text-[#49454F] dark:text-[#CAC4D0] mt-1">
              {tokenState === 'valid'
                ? 'הזן סיסמא חדשה לחשבונך'
                : resent
                ? 'נשלח קישור חדש'
                : 'בדיקת הקישור'}
            </p>
          </div>

          {tokenState === 'checking' ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-8 h-8 rounded-full border-2 border-[#1565C0] border-t-transparent animate-spin" />
              <p className="text-sm text-[#49454F] dark:text-[#CAC4D0]">בודקים את הקישור…</p>
            </div>
          ) : tokenState !== 'valid' ? (
            <div className="space-y-4">
              {resent ? (
                <div className="flex items-start gap-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/50 text-[#1B5E20] dark:text-[#81C784] px-4 py-3 rounded-xl text-sm">
                  <MailCheck size={18} className="mt-0.5 shrink-0" />
                  <span>
                    שלחנו קישור חדש לכתובת המייל שלך. הקישור תקף ל-24 שעות — מומלץ לפתוח אותו מיד.
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-[#B00020] dark:text-[#CF6679] px-4 py-3 rounded-xl text-sm">
                    <span className="mt-0.5">⚠️</span>
                    <span>
                      {linkError ||
                        (tokenState === 'expired'
                          ? 'תוקף הקישור פג. אפשר לשלוח לעצמך קישור חדש.'
                          : 'הקישור אינו תקין. בקשו קישור חדש.')}
                    </span>
                  </div>

                  {/* שליחה מחדש אפשרית רק כשהטוקן עדיין קיים במסד (פג תוקף).
                      טוקן שנוצל או שהוחלף בחדש יותר כבר לא מזוהה — שם מפנים
                      לבקשה רגילה עם כתובת מייל. */}
                  {tokenState === 'expired' ? (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resending}
                      className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-gradient-to-l from-[#6200EA] to-[#1565C0] text-white font-semibold text-base shadow-md-2 hover:shadow-md-3 hover:opacity-95 active:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resending ? (
                        <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      ) : (
                        'שלחו לי קישור חדש'
                      )}
                    </button>
                  ) : null}
                </>
              )}

              <div className="text-center">
                <Link to="/forgot-password" className="text-[#1565C0] hover:underline text-sm font-medium">
                  בקשת קישור עם כתובת מייל אחרת
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New password */}
              <div className="relative">
                <label className="block text-xs font-semibold text-[#49454F] dark:text-[#CAC4D0] mb-1.5 mr-1">
                  סיסמא חדשה
                </label>
                <div className="relative">
                  <Lock size={17} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] pointer-events-none" />
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="לפחות 8 תווים, אות וספרה"
                    className="md-input pr-11 pl-11"
                    style={{ direction: 'ltr', textAlign: 'right', paddingRight: '2.75rem', paddingLeft: '2.75rem' }}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] hover:text-[#1565C0] transition-colors duration-200"
                  >
                    {showNew ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div className="relative">
                <label className="block text-xs font-semibold text-[#49454F] dark:text-[#CAC4D0] mb-1.5 mr-1">
                  אימות סיסמא
                </label>
                <div className="relative">
                  <Lock size={17} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] pointer-events-none" />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="הזן שוב את הסיסמא"
                    className="md-input pr-11 pl-11"
                    style={{ direction: 'ltr', textAlign: 'right', paddingRight: '2.75rem', paddingLeft: '2.75rem' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] hover:text-[#1565C0] transition-colors duration-200"
                  >
                    {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-[#B00020] dark:text-[#CF6679] px-4 py-3 rounded-xl text-sm">
                  <span className="mt-0.5">⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-gradient-to-l from-[#6200EA] to-[#1565C0] text-white font-semibold text-base shadow-md-2 hover:shadow-md-3 hover:opacity-95 active:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loading ? (
                  <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  'אפס סיסמא'
                )}
              </button>
            </form>
          )}

          <p className="text-center text-xs text-[#BDBDBD] dark:text-[#49454F] mt-6">
            מערכת ניהול מאובטחת · Amutech © 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
